import { FormComponentType } from "@/components/forms/Form/convertFormData";
import { ActionCollectionReturn, ActionReturn, Trigger } from "@/components/forms/Form/types";
import { Option } from "@/components/forms/inputs/Autocomplete/types";
import { useStore } from "@/store";
import isEqual from "lodash/isEqual";
import Vue from "vue";

/**
 * Funktion som låter ett fält förändras när ett annat fält ändras
 */
export const trigger = async ({
    fieldName,
    dependentOn,
    oldValues,
    newValues,
    registry,
    setFieldValue,
    action,
    setFieldInitialValue,
    setFormValues,
    initial,
    configModuleName,
    configSubModuleName,
}: {
    fieldName: Trigger["fieldName"];
    dependentOn: Trigger["dependentOn"];
    oldValues: {
        [key: string]: unknown;
    };
    newValues: {
        [key: string]: unknown;
    };
    /**
     * Funktion från KForm för sättning av värde på enskilda fält
     */
    setFieldValue: (props: { name: string; value: unknown }) => void;
    /**
     * Funktion från KForm för sättning av värden på flera fält
     */
    setFormValues: (props: {
        /**
         * key = namn på fältet, value = nya värdet
         */
        values: {
            [key: string]: unknown;
        };
        /**
         * Ska fält som inte skickats med resettas? Om falskt så kommer bara fält som skickats med påverkas
         */
        reset: boolean;
    }) => void;
    action: Trigger["action"];
    registry: Map<
        string,
        Vue & {
            $parent: {
                kType: FormComponentType;
                setDisabled: ({ value }: { value: boolean }) => void;
                setOverrideRequired: ({ value }: { value: boolean | null }) => void;
                getSelectedOption?: () => undefined | unknown | unknown[];
                setOptions?: ({ options, setDefault }: { options: Option[]; setDefault?: boolean }) => void;
                setFieldInitialValue: (props: { name: string; value: unknown }) => void;
                setSuffix: ({ value }: { value: string | null }) => void;
                setUrlParams: ({ value }: { [key: string]: unknown }) => void;
                setSuffixType: ({ value }: { value: null | "info" | "info-dark" | "note" }) => void;
            };
        }
    >;
    /**
     * Funktion från KForm för att sätta  värde på ett fält i initial form data. Kan användas för att lägga till
     * defaultvärden på ett fält som görs async
     * (t.ex förifylld rad i postoperativ komplikation i opjournalen, som görs async)
     */
    setFieldInitialValue: (props: { name: string; value: unknown }) => void;
    /**
     * Körs triggern vid första sättning av data på formuläret? Kan kollas på för att undvika att triggern
     * kör vid första inläsning (t.ex vid redigering)
     */
    initial?: boolean;
    configModuleName?: string;
    configSubModuleName?: string;
}) => {
    /**
     * Om värdet på fältet/fälten som lyssnas på har förändrats.
     * dependentOnValue kommer vara ett objekt med key namn på ett av fälten som lyssnas på och value dess värde
     */
    if (
        (Array.isArray(dependentOn) &&
            dependentOn.some((dependentOnName) => !isEqual(newValues[dependentOnName], oldValues[dependentOnName]))) ||
        !isEqual(newValues[dependentOn as string], oldValues[dependentOn as string])
    ) {
        /**
         * Anropa funktionen och ta emot resultatet
         */
        const actionData = await action({
            initial,
            /*
             * Värde(n) på lyssnarfält
             */
            value: Array.isArray(fieldName)
                ? Object.fromEntries(fieldName.map((name) => [name, newValues[name]]))
                : newValues[fieldName],
            /*
             * Värde(n) på förändrade fält
             */
            dependentOnValue: Array.isArray(dependentOn)
                ? Object.fromEntries(
                      dependentOn.map((dependentOnName) => [dependentOnName, newValues[dependentOnName]])
                  )
                : newValues[dependentOn],
            /*
             * Valda options på förändrade fält, om såna finns
             */
            selectedOption: (Array.isArray(dependentOn)
                ? Object.fromEntries(
                      dependentOn.map((dependentOnName) => [
                          dependentOnName,
                          registry?.get?.(dependentOnName)?.$parent?.getSelectedOption?.(),
                      ])
                  )
                : registry?.get?.(dependentOn)?.$parent?.getSelectedOption?.()) as any,
        });

        if (!actionData) {
            return;
        }

        const store = useStore();

        /*
         * Om registry är satt. Då kan vi göra det som är beroende av det, vilket är att anropa funktioner satta
         * på K-komponenterna (t.ex setDisabled)
         */
        if (registry) {
            if (Array.isArray(actionData) || Array.isArray(fieldName)) {
                if (Array.isArray(actionData) && Array.isArray(fieldName)) {
                    actionData.forEach((data) => {
                        // Trigger ska inte köras på fält som är dolda via inställningar
                        if (
                            !store.getters["config/shouldBeVisibleByName"]({
                                moduleName: configModuleName,
                                subModuleName: configSubModuleName,
                                name: fieldName,
                            })
                        ) {
                            return;
                        }

                        const field = registry.get(data.fieldName);

                        /**
                         * Ändra attributet "disabled" på komponenten, om det är satt
                         */
                        if (typeof data.disabled === "boolean") {
                            field?.$parent?.setDisabled?.({ value: data.disabled });
                        }

                        /**
                         * Ändra required i validation. Overridar eventuella inställningar eller hårdkodning
                         */
                        if (data.required === null || typeof data.required === "boolean") {
                            field?.$parent?.setOverrideRequired?.({ value: data.required });
                        }

                        /**
                         * Ändra options
                         */
                        if (Array.isArray(data.options)) {
                            field?.$parent?.setOptions?.({ options: data.options, setDefault: data.setOptionsDefault });
                        }

                        if (data.suffix !== undefined) {
                            const field = registry.get(data.fieldName);

                            field?.$parent?.setSuffix?.({ value: data.suffix });
                        }

                        if (data.suffixType !== undefined) {
                            const field = registry.get(data.fieldName);

                            field?.$parent?.setSuffixType?.({ value: data.suffixType });
                        }

                        /**
                         * Sätter nya urlParams på KAutocomplete. Sätt reactiveUrlParams true på komponenten för att
                         * få den att automatiskt göra en ny hämtning av options vid sättning
                         */
                        if (data.urlParams !== undefined) {
                            field?.$parent?.setUrlParams({ value: data.urlParams });
                        }
                    });
                }
                // Trigger ska bara köras på fält som inte är dolda via inställningar
            } else if (
                store.getters["config/shouldBeVisibleByName"]({
                    moduleName: configModuleName,
                    subModuleName: configSubModuleName,
                    name: fieldName,
                })
            ) {
                const field = registry.get(fieldName);

                /**
                 * Ändra attributet "disabled" på komponenten, om det är satt
                 */
                if (typeof actionData.disabled === "boolean") {
                    field?.$parent?.setDisabled?.({ value: actionData.disabled });
                }

                /**
                 * Ändra required i validation. Overridar eventuella inställningar eller hårdkodning
                 */
                if (actionData.required === null || typeof actionData.required === "boolean") {
                    field?.$parent?.setOverrideRequired?.({ value: actionData.required });
                }

                /**
                 * Ändra options
                 */
                if (Array.isArray(actionData.options)) {
                    field?.$parent?.setOptions?.({
                        options: actionData.options,
                        setDefault: actionData.setOptionsDefault,
                    });
                }

                if (actionData.suffix !== undefined) {
                    const field = registry.get(fieldName);

                    field?.$parent?.setSuffix?.({ value: actionData.suffix });
                }

                if (actionData.suffixType !== undefined) {
                    const field = registry.get(fieldName);

                    field?.$parent?.setSuffixType?.({ value: actionData.suffixType });
                }

                /**
                 * Sätter nya urlParams på KAutocomplete. Sätt reactiveUrlParams true på komponenten för att
                 * få den att automatiskt göra en ny hämtning av options vid sättning
                 */
                if (actionData.urlParams !== undefined) {
                    field?.$parent?.setUrlParams?.({ value: actionData.urlParams });
                }
            }
        }

        /*
         * Sättning av värden i formuläret (vid array på både fält som ska ändras och olika värden för varje fält)
         */
        if (Array.isArray(actionData) && Array.isArray(fieldName)) {
            setFormValues({
                values: Object.fromEntries(
                    fieldName
                        .filter((name) => {
                            // Trigger ska bara köras på fält som inte är dolda via inställningar
                            return store.getters["config/shouldBeVisibleByName"]({
                                name,
                                moduleName: configModuleName,
                                subModuleName: configSubModuleName,
                            });
                        })
                        .map((name) => {
                            const newValue = (actionData as NonNullable<ActionCollectionReturn>).find(
                                (data) => data.fieldName === name
                            )?.value;

                            return [name, newValue];
                        })
                ),
                reset: false,
            });

            return;
        }

        /*
         * Sättning av värden i formuläret (vid array av fält som ska ändras och samma data som ska sättas på alla fält)
         */
        if (!Array.isArray(actionData) && Array.isArray(fieldName) && typeof actionData.value !== "undefined") {
            setFormValues({
                values: Object.fromEntries(
                    fieldName
                        .filter((name) => {
                            // Trigger ska bara köras på fält som inte är dolda via inställningar
                            return store.getters["config/shouldBeVisibleByName"]({
                                name,
                                moduleName: configModuleName,
                                subModuleName: configSubModuleName,
                            });
                        })
                        .map((name) => {
                            return [name, (actionData as NonNullable<ActionReturn>).value];
                        })
                ),
                reset: false,
            });
        }

        /*
         * Sätt nytt värde på fältet, om det är satt (vid icke-array).
         * Kommer bara köras på fält som inte är dolda via inställningar
         */
        if (
            !Array.isArray(actionData) &&
            !Array.isArray(fieldName) &&
            typeof actionData.value !== "undefined" &&
            store.getters["config/shouldBeVisibleByName"]({
                name,
                moduleName: configModuleName,
                subModuleName: configSubModuleName,
            })
        ) {
            setFieldValue({
                name: fieldName,
                value: actionData.value,
            });

            /**
             * Sätt nya värdet som initial (används för att jämföra mot vid osparad data check
             */
            if (actionData.setAsInitial) {
                setFieldInitialValue({
                    name: fieldName,
                    value: actionData.value,
                });
            }
        }
    }
};
